<template>
    <Dialog v-model:open="opened">
        <DialogTrigger> <slot /> </DialogTrigger>
        <DialogContent>
            <DialogHeader>
                <DialogTitle>
                    <span v-if="dayOffId">
                        {{ $t("days_off.edit_days_off_modal.title") }}
                    </span>
                    <span v-else>
                        {{ $t("days_off.add_days_off_modal.title") }}
                    </span></DialogTitle
                >
            </DialogHeader>

            <div class="mt-2">
                <div class="mt-4">
                    <label
                        for="workflow-name"
                        class="block text-sm font-medium text-iron-700"
                    >
                        {{ $t("days_off.add_days_off_modal.name") }}</label
                    >
                    <div class="mt-1 w-full">
                        <input
                            v-model="name"
                            type="text"
                            data-cy="new-availability-name"
                            name="availability-name"
                            class="block w-full rounded-md border-iron-300 shadow-sm focus:border-z-orange-500 focus:ring-z-orange-500 sm:text-sm"
                            :placeholder="
                                $t('days_off.add_days_off_modal.placeholder')
                            "
                        />
                    </div>
                </div>

                <div class="flex items-end gap-4">
                    <div class="mt-4 w-full">
                        <label
                            for="day-off-start"
                            class="block text-sm font-medium text-iron-700"
                        >
                            {{ $t("days_off.add_days_off_modal.start") }}</label
                        >
                        <div class="mt-1 w-full">
                            <DatePicker
                                v-model="start"
                                :locale="locale === 'cs' ? 'cs' : 'en'"
                                :min-date="new Date()"
                                :placeholder="$t('days_off.start_date')"
                            ></DatePicker>
                        </div>
                    </div>
                </div>

                <div class="mt-4">
                    <label
                        for="day-off-start"
                        class="block text-sm font-medium text-iron-700"
                    >
                        {{ $t("days_off.add_days_off_modal.end") }}</label
                    >
                    <div class="mt-1 w-full">
                        <DatePicker
                            v-model="end"
                            :locale="locale === 'cs' ? 'cs' : 'en'"
                            :min-date="minDate"
                            :placeholder="$t('days_off.end_date')"
                        ></DatePicker>
                    </div>
                </div>
            </div>

            <DialogFooter>
                <Button variant="outline" @click="closeModal">
                    {{ $t("cancel") }}
                </Button>
                <Button v-if="!dayOffId" @click="addDayOff">{{
                    $t("days_off.add_days_off_modal.title")
                }}</Button>
                <Button v-else @click="updateDayOff">{{
                    $t("days_off.edit_days_off_modal.title")
                }}</Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { Button } from "@/components/ui/button";
import { ref, computed, watch } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import DatePicker from "@/components/ui/date-picker/index.vue";
import { useI18n } from "vue-i18n";
import { TimePicker } from "@/components/ui/time-picker";

import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";

const { locale } = useI18n();

const date = ref();
const props = defineProps({
    dayOffId: {
        type: Number,
        required: false,
    },
    dayOffName: {
        type: String,
        required: false,
    },
    dayOffStart: {
        type: String,
        required: false,
    },
    dayOffEnd: {
        type: String,
        required: false,
    },
});

const opened = ref(false);
const name = ref(props.dayOffName ?? "");
const start = ref(props.dayOffStart ?? "");
const end = ref(props.dayOffEnd ?? "");

const minDate = computed(() => {
    return new Date(start.value);
});

watch(start, () => {
    if (new Date(end.value) < new Date(start.value)) {
        end.value = start.value;
    }
});

const closeModal = () => {
    opened.value = false;
};

const addDayOff = function () {
    router.post(
        route("app.days_off.store"),
        {
            name: name.value,
            start: new Date(start.value).toISOString(),
            end: new Date(end.value).toISOString(),
        },
        {
            onFinish: () => {
                closeModal();
            },
        },
    );
};

const updateDayOff = function () {
    router.put(
        route("app.days_off.update", {
            dayOff: props.dayOffId,
        }),
        {
            name: name.value,
            start: new Date(start.value).toUTCString(),
            end: new Date(end.value).toUTCString(),
        },
        {
            onFinish: () => {
                closeModal();
            },
        },
    );
};
</script>
